import api from './api';

// API client for the manual "Nifty Buying" feature (single-leg NIFTY CE/PE buy, no hedge).
// Kept in its own file, same convention as spreadApi.ts / optionSellingApi.ts.

export type BuyingDirection = 'BULLISH' | 'BEARISH';
export type BuyingMarket = 'NIFTY' | 'SENSEX';

export interface PlaceBuyingRequest {
  market: BuyingMarket;
  direction: BuyingDirection;
  expiryDate: string; // 'YYYY-MM-DD'
  capitalPercentage: number;
  rangeMin: number;
  rangeMax: number;
  slippagePercentage?: number;
  delaySeconds?: number;
  slPoints?: number;
  tslNpPoints?: number;
  targetPoints?: number;
  slXYOffset?: number;
  // TRUE trailing stop on the live X-count (Bullish) / Y-count (Bearish) breadth signal — exits
  // once the live count gives back this many points from its best-seen value since entry.
  tslXYPoints?: number;
  broker?: string;
}

export interface StrikeBuyResult {
  configId: number;
  strike: number;
  symbol: string;
  success: boolean;
  quantity?: number;
  price?: number;
  orderId?: string;
  status?: string;
  error?: string;
}

// Every broker enabled for Nifty Buying in Broker Management gets its own full, independent order
// set on one Buy click — each sized off that broker's OWN configured Capital Amount (% Cap is a
// percentage of it), split EQUALLY across every strike that qualifies the Range Min/Max filter.
export interface BrokerBuyResult {
  broker: string;
  totalDeployAmount: number;
  perStrikeAmount: number;
  qualifyingStrikeCount: number;
  results: StrikeBuyResult[];
  error?: string; // set when this broker was skipped entirely (e.g. no Capital Amount configured)
}

export interface BuyingResult {
  success: boolean;
  brokerResults: BrokerBuyResult[];
}

export interface NiftyBuyingConfig {
  id: number;
  user_id: number;
  name: string;
  market: BuyingMarket;
  direction: BuyingDirection;
  expiry_date: string;
  is_active: boolean | number;
  entry_active: boolean | number;
  exit_active: boolean | number;
  capital_percentage: number;
  range_min: number;
  range_max: number;
  slippage_percentage: number;
  delay_seconds: number;
  broker: string;
  sl_points: number | null;
  tsl_np_points: number | null;
  tsl_np_peak: number | null;
  target_points: number | null;
  sl_xy_threshold: number | null;
  entry_spot: number | null;
  created_at: string;
  updated_at: string;
  entrySymbol?: string;
  entryPrice?: number;
  currentLtp?: number | null;
  roiPercent?: number | null;
  spotDiff?: number | null;
  currentSpot?: number | null;
  quantity?: number;
}

export const niftyBuyingAPI = {
  placeOrder: (payload: PlaceBuyingRequest) => api.post<{ message: string } & BuyingResult>('/nifty-buying/place', payload),
  exitPosition: (configId: number) => api.post(`/nifty-buying/${configId}/exit`),
  exitAll: () => api.post('/nifty-buying/exit-all'),
  // Per-panel "Exit All" — just this market+direction's open strikes, leaving the other
  // direction and the other market's positions untouched.
  exitAllForDirection: (market: BuyingMarket, direction: BuyingDirection) =>
    api.post(`/nifty-buying/exit-all/${market}/${direction}`),
  // Marks a position closed in our records only (no real order) — for one already closed
  // manually in the Dhan app directly, where a normal Exit would just keep failing/retrying.
  clearPosition: (configId: number) => api.post(`/nifty-buying/${configId}/clear`),
  clearAll: () => api.post('/nifty-buying/clear-all'),
  getOpenPositions: () => api.get<NiftyBuyingConfig[]>('/nifty-buying/positions/open'),
  getOrderHistory: (page = 1, limit = 50, market?: BuyingMarket) =>
    api.get(`/nifty-buying/orders?page=${page}&limit=${limit}${market ? `&market=${market}` : ''}`),
  clearOrderHistory: () => api.delete('/nifty-buying/orders/clear'),
  deleteOrdersPermanently: () => api.delete('/nifty-buying/orders/delete-permanently'),
  // Spot-only poll — zero Dhan optionchain/quote calls (reuses the already-maintained live-feed
  // cache), for places that only need the plain spot number without the full ATM Delta scan's
  // heavier Dhan rate-limit cost (see niftyBuyingController.ts's getSpot for why this matters).
  getSpot: (market: BuyingMarket) => api.get<{ spot: number | null }>(`/nifty-buying/spot?market=${market}`),
  getAtmGrid: (expiryDate: string, market: BuyingMarket = 'NIFTY') => api.get<AtmGridResponse>(`/nifty-buying/atm-grid?expiryDate=${expiryDate}&market=${market}`),
  getAtmDelta: (expiryDate: string, market: BuyingMarket = 'NIFTY') => api.get<AtmDeltaResponse>(`/nifty-buying/atm-delta?expiryDate=${expiryDate}&market=${market}`),
  getLastExpiry: (market: BuyingMarket) => api.get<{ expiry_date: string | null }>(`/nifty-buying/last-expiry?market=${market}`),
  saveLastExpiry: (market: BuyingMarket, expiryDate: string) => api.put('/nifty-buying/last-expiry', { market, expiry_date: expiryDate }),
};

export interface AtmGridRow { strike: number; isAtm: boolean; ce: number; pe: number; }
export interface AtmGridResponse { spot: number; atmBase: number; grid: AtmGridRow[]; }

export interface AtmDeltaSide { strike: number; delta: number | null; ltp: number | null; pctFromLow: number | null; pctFromHigh: number | null; }
export interface AtmDeltaResponse { spot: number; atmStrike: number; ce: AtmDeltaSide; pe: AtmDeltaSide; }

export interface NiftyBuyingFilterSettings {
  user_id: number;
  nl_nh_enabled: boolean | number;
  n_intra_points: number | null;
  spot_gate_enabled: boolean | number;
  n_pct_l_threshold: number | null;
  bnl_threshold: number | null;
  itl_threshold: number | null;
}

export const niftyBuyingFilterAPI = {
  getSettings: () => api.get<NiftyBuyingFilterSettings>('/nifty-buying/filter-settings'),
  saveSettings: (settings: Partial<NiftyBuyingFilterSettings>) => api.put('/nifty-buying/filter-settings', settings),
};

export interface NiftyBuyingDefault {
  market?: BuyingMarket;
  direction: BuyingDirection;
  capital_percentage: number | null;
  range_min: number | null;
  range_max: number | null;
  slippage_percentage: number | null;
  delay_seconds: number | null;
  sl_points: number | null;
  tsl_np_points: number | null;
  target_points: number | null;
  sl_xy_offset: number | null;
  tsl_xy_points: number | null;
  broker: string;
}

export const niftyBuyingDefaultsAPI = {
  getDefaults: () => api.get<NiftyBuyingDefault[]>('/nifty-buying/manual-defaults'),
  saveDefault: (body: NiftyBuyingDefault) => api.put('/nifty-buying/manual-defaults', body),
  // Erases the saved default itself (not just the on-screen fields) — otherwise a page refresh
  // would silently bring the old values straight back, since the panel auto-loads whatever's
  // saved on mount.
  clearDefault: (market: BuyingMarket, direction: BuyingDirection) =>
    api.delete(`/nifty-buying/manual-defaults?market=${market}&direction=${direction}`),
};

export interface NiftyBuyingReportInfo {
  date: string; // 'YYYY-MM-DD'
  label: 'Today' | 'Yesterday';
  // Client's explicit ask: only usable after market close (3:30 PM IST) — a past date is always
  // ready; "Today" flips to ready the moment the backend's own 3:30 scheduler generates it.
  ready: boolean;
}

// Client's explicit ask: Dashboard-facing daily PDF (today's full order/fail details + an
// automated check on whether the bugs fixed on 2026-11-04 have recurred). Fetched as a blob
// (not a plain `window.open(url)`) because this API requires the same Bearer-token header every
// other call here needs — a bare new-tab navigation can't attach it, so the page builds its own
// object URL from an authenticated fetch instead.
export const niftyBuyingReportsAPI = {
  list: () => api.get<{ reports: NiftyBuyingReportInfo[] }>('/nifty-buying/reports'),
  fetchPdfBlob: (date: string, mode: 'view' | 'download') =>
    api.get(`/nifty-buying/reports/${date}/${mode}`, { responseType: 'blob' }),
};
